import React from 'react';
import { deleteField, doc, updateDoc } from 'firebase/firestore';

import ConfirmationModal from '../ConfirmationModal';
import { AuthContext } from '../../context/AuthContext';
import { EVENT_NAMES } from '../../utils/eventLogging/eventNames';
import { firestore } from '../../context/FirebaseConfig';
import { useTranslation } from '../../i18n/i18n';
import Select from '../ui/Select';
import UserModalShell from './UserModalShell';

function buildUserOptions(users) {
  if (!users || Object.keys(users).length === 0) return [];

  return Object.entries(users)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, value]) => ({
      value: key,
      label: `${value?.firstname ?? ''} ${value?.lastname ?? ''}`.trim(),
    }));
}

export default function DeleteUserModal({ settings, onClose }) {
  const { currentUser, logEventCustom } = React.useContext(AuthContext);
  const { t } = useTranslation(currentUser?.info?.languageCode);
  const [selectedUserToDelete, setSelectedUserToDelete] = React.useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);

  const isManager =
    currentUser?.info?.user_flags?.includes('manager') || currentUser?.info?.user_flags?.includes('admin');

  const userOptions = buildUserOptions(settings?.users);

  async function removeUser() {
    if (!selectedUserToDelete || !isManager) return;

    try {
      const settingsDoc = doc(
        firestore,
        'clients',
        currentUser.company.company,
        'client_settings',
        'bale_feed_settings',
      );
      await updateDoc(settingsDoc, {
        [`users.${selectedUserToDelete}`]: deleteField(),
      });

      // After the delete resolves, so a failed removal is never counted as one.
      logEventCustom(EVENT_NAMES.OPERATOR_DELETE, { operator_id: selectedUserToDelete });

      setShowDeleteConfirmation(false);
      setSelectedUserToDelete('');
      onClose?.();
    } catch (error) {
      console.error('Error removing user:', error);
    }
  }

  return (
    <>
      <UserModalShell
        title={t('delete_user_modal_title')}
        onClose={onClose}
        footer={
          <button
            type='button'
            disabled={!selectedUserToDelete || !isManager}
            className='flex items-center justify-center text-lg leading-[120%] font-medium text-[#FFFFFF] dark:text-[#FAFAFA] rounded-lg bg-[#DA1E28] hover:bg-[#C41A24] active:bg-[#DA1E28] dark:bg-[#E04B4B] dark:hover:bg-[#D32F2F] dark:active:bg-[#E04B4B] py-2 px-4 min-h-12 box-border transition-all duration-300 disabled:opacity-50 disabled:pointer-events-none'
            onClick={() => setShowDeleteConfirmation(true)}
          >
            {t('delete_user_modal_action')}
          </button>
        }
      >
        <div className='flex flex-col gap-1.5'>
          <label className='text-base leading-[120%] text-[#464255] dark:text-[#FAFAFA]'>
            {t('delete_user_modal_select_placeholder')}
          </label>
          <Select
            className='w-full'
            placeholder={t('delete_user_modal_select_placeholder')}
            selectedValue={selectedUserToDelete ? undefined : t('delete_user_modal_select_placeholder')}
            value={selectedUserToDelete}
            options={userOptions}
            onChange={setSelectedUserToDelete}
            disabled={!isManager}
          />
        </div>
      </UserModalShell>

      {showDeleteConfirmation && (
        <ConfirmationModal
          title={t('delete_user_modal_confirm_title')}
          subTitle={t('delete_user_modal_confirm_message')}
          handleCancel={() => setShowDeleteConfirmation(false)}
          handleConfirm={removeUser}
        />
      )}
    </>
  );
}
